import React, { useEffect, useMemo, useState } from "react";
import { toast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, CheckCircle, RefreshCw, ExternalLink, Trash2 } from "lucide-react";
import { SocialWizardDialog } from "@/components/socials/SocialWizardDialog";

const PLATFORM_ICONS = {
  TikTok: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23ff0050' d='M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.72a8.19 8.19 0 004.76 1.52V6.79a4.83 4.83 0 01-1-.1z'/%3E%3C/svg%3E",
  Instagram: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/Instagram_icon.png/120px-Instagram_icon.png",
  YouTube: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/YouTube_full-color_icon_%282017%29.svg/120px-YouTube_full-color_icon_%282017%29.svg.png",
};

export function SocialsPage() {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  const [wizardOpen, setWizardOpen] = useState(false);

  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifySocialId, setVerifySocialId] = useState(null);
  const [verifyCode, setVerifyCode] = useState(null);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  async function load() {
    setError(null);
    try {
      const res = await api.get("/settings/me");
      setSettings(res.data);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  useEffect(() => { load(); }, []);

  // Handle Instagram OAuth callback redirect (?instagram=connected|error)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const connected = searchParams.get("instagram");
    const err = searchParams.get("instagram_error");
    if (connected === "connected") {
      const handle = searchParams.get("handle") || "";
      toast({ title: "Instagram connected!", description: handle ? `@${handle} is now verified.` : "Your Instagram account is now verified." });
      load();
      // clean the URL
      searchParams.delete("instagram");
      searchParams.delete("handle");
      setSearchParams(searchParams, { replace: true });
    } else if (err) {
      toast({ title: "Instagram connection failed", description: `Reason: ${err}. Please try again.` });
      searchParams.delete("instagram_error");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const socials = useMemo(() => settings?.socials || [], [settings]);
  const verified = socials.filter((s) => s.verification?.status === "verified" || s.status === "approved");
  const platforms = [...new Set(socials.map((s) => s.platform || "").filter(Boolean))];

  async function deleteSocial(id) {
    try {
      const res = await api.delete(`/socials/${id}`);
      setSettings(res.data);
      toast({ title: "Social removed" });
    } catch (e) {
      toast({ title: "Error", description: apiErrorMessage(e) });
    }
  }

  async function verifySocial() {
    if (!verifySocialId) return;
    setVerifyChecking(true);
    setVerifyResult(null);
    try {
      const res = await api.post("/socials/verify/check", { social_id: verifySocialId });
      setVerifyResult(res.data);
      if (res.data?.verified) {
        const fresh = await api.get("/settings/me");
        setSettings(fresh.data);
        toast({ title: "Social verified", description: "Your social account was verified via bio code." });
      }
    } catch (e) {
      setVerifyResult({ error: apiErrorMessage(e) });
    } finally {
      setVerifyChecking(false);
    }
  }

  const isVerified = (s) => s.verification?.status === "verified" || s.status === "approved";

  return (
    <div data-testid="socials-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 data-testid="socials-title" className="page-title">Social Accounts</h1>
          <p data-testid="socials-subtitle" className="page-subtitle">
            Connect and manage your accounts to start earning
          </p>
        </div>
        <Button
          data-testid="socials-add-account-btn"
          className="shrink-0 gap-1.5 rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]"
          onClick={() => setWizardOpen(true)}
        >
          <Plus size={15} /> Add Account
        </Button>
      </div>

      {error && (
        <div data-testid="socials-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">{error}</div>
      )}

      {/* Verified Accounts Overview */}
      <div data-testid="social-overview" className="rounded-2xl border border-border/60 bg-card/70 p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-lg font-bold text-foreground">{socials.length} Connected Account{socials.length !== 1 ? "s" : ""}</div>
            <div className="mt-0.5 flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle size={14} /> {verified.length} Verified
            </div>
          </div>
          <div className="flex items-center gap-2">
            {platforms.map((p) => (
              <div key={p} className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted/50">
                <img src={PLATFORM_ICONS[p]} alt={p} className="h-5 w-5 object-contain" />
              </div>
            ))}
          </div>
        </div>
        {socials.length > 0 && (
          <div className="mt-3">
            <div className="h-1.5 w-full overflow-hidden rounded-sm bg-muted/50">
              <div
                className="h-full rounded-sm bg-gradient-to-r from-emerald-500/80 to-emerald-400/50 transition-all duration-500"
                style={{ width: `${socials.length > 0 ? (verified.length / socials.length) * 100 : 0}%` }}
              />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">{verified.length} / {socials.length} verified</div>
          </div>
        )}
      </div>

      {/* Account Cards */}
      <div data-testid="socials-list" className="space-y-3">
        {socials.map((s) => (
          <div
            key={s.id}
            data-testid={`social-card-${s.id}`}
            className="rounded-2xl border border-border/60 bg-card/70 p-4"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted/50">
                {s.verification?.profile_picture_url || s.instagram_oauth?.profile_picture_url ? (
                  <img
                    src={s.verification?.profile_picture_url || s.instagram_oauth?.profile_picture_url}
                    alt={s.handle || s.platform}
                    data-testid={`social-avatar-${s.id}`}
                    className="h-10 w-10 rounded-xl object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <img src={PLATFORM_ICONS[s.platform] || ""} alt={s.platform} className="h-6 w-6 object-contain" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span data-testid={`social-handle-${s.id}`} className="text-sm font-semibold text-foreground truncate">{s.label || `@${s.handle || "unknown"}`}</span>
                  {isVerified(s) ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                      <CheckCircle size={10} /> Verified
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">Pending</span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {s.niches?.length > 0 ? s.niches.map((n) => (
                    <span key={n} className="mr-1.5 inline-block rounded-full bg-muted px-2 py-0.5 text-[10px]">{n}</span>
                  )) : <span className="text-muted-foreground/60">No niches selected</span>}
                </div>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              {s.url && (
                <a
                  data-testid={`social-profile-link-${s.id}`}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-full bg-muted/50 px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                >
                  <ExternalLink size={12} /> Profile
                </a>
              )}
              {!isVerified(s) && (
                <Button
                  data-testid={`social-verify-${s.id}`}
                  variant="ghost"
                  size="sm"
                  className="h-7 gap-1 rounded-full text-xs text-[#c8ff00] hover:bg-[#c8ff00]/10"
                  onClick={() => {
                    setVerifySocialId(s.id);
                    setVerifyCode(s.verification?.code || null);
                    setVerifyResult(null);
                    setVerifyOpen(true);
                  }}
                >
                  <RefreshCw size={12} /> Verify
                </Button>
              )}
              <Button data-testid={`social-delete-${s.id}`} variant="ghost" size="sm" className="h-7 gap-1 rounded-full text-xs text-rose-500 hover:bg-rose-500/10" onClick={() => deleteSocial(s.id)}>
                <Trash2 size={12} /> Delete
              </Button>
            </div>
          </div>
        ))}

        {!socials.length && (
          <div
            data-testid="socials-empty"
            className="rounded-2xl border border-dashed border-border/60 px-6 py-10 text-center"
          >
            <div className="text-sm font-medium text-muted-foreground">No accounts connected yet</div>
            <div className="mt-1 text-xs text-muted-foreground/60">Add your first social account to start earning</div>
            <Button
              className="mt-4 gap-1.5 rounded-full bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]"
              onClick={() => setWizardOpen(true)}
            >
              <Plus size={15} /> Add Account
            </Button>
          </div>
        )}
      </div>

      {/* Add Account Wizard (new stepped design) */}
      <SocialWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onCompleted={load}
      />

      {/* Verify Dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent data-testid="verify-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verify Social Account</DialogTitle>
            <DialogDescription data-testid="socials-verify-dialog-description">Add the generated code to your profile bio, then run the check.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {verifyCode ? (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">Add this code to your bio, then click "Check verification":</div>
                <div data-testid="verify-code-display" className="rounded-lg border border-border/60 bg-muted/50 p-3 font-mono text-sm">{verifyCode}</div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No verification code available.</div>
            )}
            {verifyResult && (
              <div>
                {verifyResult.error ? (
                  <div className="rounded-lg border border-rose-500/20 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300">{verifyResult.error}</div>
                ) : verifyResult.verified ? (
                  <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-300">Verified! Your social account is now verified.</div>
                ) : (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">Code not found in bio yet. Try again after adding it.</div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setVerifyOpen(false)}>Cancel</Button>
              <Button onClick={verifySocial} disabled={verifyChecking} className="bg-[#c8ff00] font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]">
                {verifyChecking ? "Checking..." : "Check verification"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
